<template>
  <div class="space-y-32">
    <h1 class="heading-1">{{ i18n(translations.title) }}</h1>
    <div>
      <h2 class="heading-3 mb-16 md:mb-12">
        {{ i18n(translations.shippingAddressTitle) }}
      </h2>
      <RevCard class="p-24">
        <Address
          :address="replacementOrder.shippingAddress"
          data-qa="shipping-address"
          data-test="shipping-address"
          :with-details="true"
        />
      </RevCard>
    </div>
    <RevInfoBlock
      :content="i18n(translations.infoBlockContent)"
      :cta="i18n(translations.infoBlockCta)"
      data-qa="other-options-info-block"
      data-test="other-options-info-block"
      :icon="IconHome"
      :title="i18n(translations.infoBlockTitle)"
      variant="info"
      @click="goToResolutionOptions"
    />
    <div>
      <h2 class="heading-3 mb-16 md:mb-36">
        {{ i18n(translations.deliveryOptionTitle) }}
      </h2>
      <XrDeliveryOption
        :carrier-name="replacementOrder.carrierName"
        class="p-24"
        data-qa="delivery-option"
        data-test="delivery-option"
        :date-max="replacementOrder.deliveryDatesWindow.max"
        :date-min="replacementOrder.deliveryDatesWindow.min"
        :image="replacementOrder.product.image.href"
        :title="replacementOrder.product.title"
      />
    </div>
    <div>
      <RevButton
        class="mb-16"
        data-qa="continue-cta"
        data-test="continue-cta"
        full-width="always"
        variant="primary"
        @click="goToReturnFunnel"
        >{{ i18n(translations.continueCta) }}</RevButton
      >
      <FormattedMessage
        class="text-static-default-low body-2 mb-72"
        :definition="translations.dataPolicy"
        tag="p"
      >
        <template #link>
          <RevLink
            data-qa="data-protection-link"
            data-test="data-protection-link"
            @click="goToDataProtection"
            >{{ i18n(translations.dataPolicyLink) }}
          </RevLink>
        </template>
      </FormattedMessage>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  navigateTo,
  useAsyncData,
  useRoute,
  useRouteLocationWithLocale,
} from '#imports'
import { computed, inject, onMounted, toRaw } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

import Address from '@backmarket/nuxt-module-address/Address.vue'
import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevLink } from '@ds/components/Link'
import { IconHome } from '@ds/icons/IconHome'

import type { GetExpressReplacementResponse } from '~/api/express-replacement/types/GetExpressReplacement.types'
import type { ResolutionOption } from '~/api/resolution-engine/types/GetResolutionOptions.types'
import { CMS } from '~/scopes/cms/routes-names'
import XrDeliveryOption from '~/scopes/customer-care/resolution/components/ExpressReplacement/shipping/XrDeliveryOption/XrDeliveryOption.vue'
import { useCreateShipmentApi } from '~/scopes/customer-care/resolution/composables/useCreateShipmentApi'
import type { ProvideResolutionFlowMachine } from '~/scopes/customer-care/resolution/machine/ResolutionFlow.machine.types'
import { ResolutionFlowStateMachineKey } from '~/scopes/customer-care/resolution/symbols'
import { CUSTOMER_CARE } from '~/scopes/customer-care/route-names'
import { LEGAL_PAGE_NAME } from '~/scopes/legal/constants'

import translations from './ExpressReplacementShipping.translations'

const resolutionFlowStateMachine = inject(
  ResolutionFlowStateMachineKey,
) as ProvideResolutionFlowMachine
const { params } = useRoute()
const i18n = useI18n()
const resolveLocalizedRoute = useRouteLocationWithLocale()
const createShipmentKey = getAsyncDataKey(
  'express-replacement-shipping-create-shipment',
  {
    orderlineId: params.orderlineId,
  },
)

const { data, error } = await useAsyncData(
  createShipmentKey,
  () =>
    useCreateShipmentApi({
      declaredProblem: {
        declaredWarrantyState:
          resolutionFlowStateMachine.state.value.context.customerInput
            .declaredWarrantyState,
        orderlineId: Number(params.orderlineId),
        customerIssues:
          resolutionFlowStateMachine.state.value.context
            .diagnosisV2CustomerIssues,
      },
      resolutionOption: (
        resolutionFlowStateMachine.state.value.context
          .resolutionOptions as ResolutionOption[]
      )[0],
    }),
  {
    immediate: !resolutionFlowStateMachine.state.value.context.shipment,
    default: () => ({
      shipment: resolutionFlowStateMachine.state.value.context.shipment,
    }),
  },
)

if (
  data.value.shipment &&
  !resolutionFlowStateMachine.state.value.context.shipment
) {
  resolutionFlowStateMachine.send({
    type: 'STORE_SHIPMENT',
    data: {
      shipment: toRaw(data.value.shipment),
    },
  })
}

onMounted(() => {
  if (error.value) {
    resolutionFlowStateMachine.send('ERROR')
  }
})

const replacementOrder = computed(
  () =>
    resolutionFlowStateMachine.state.value.context.xrData
      ?.replacementOrder as GetExpressReplacementResponse['replacementOrder'],
)

const goToResolutionOptions = async () => {
  await navigateTo(
    resolveLocalizedRoute({
      name: CUSTOMER_CARE.RESOLUTION.SELF,
    }) as RouteLocationRaw,
  )
}

const goToReturnFunnel = async () => {
  await navigateTo(
    resolveLocalizedRoute({
      name: CUSTOMER_CARE.RESOLUTION.EXPRESS_REPLACEMENT.DATA_REMOVAL,
    }) as RouteLocationRaw,
  )
}

const goToDataProtection = async () => {
  await navigateTo(
    resolveLocalizedRoute({
      name: CMS.LEGAL_PAGE,
      params: {
        pageName: LEGAL_PAGE_NAME.DATA_PROTECTION,
      },
    }) as RouteLocationRaw,
    { open: { target: '_blank' } },
  )
}
</script>
